import i18n from '../../i18n';
import type { SupportedLanguage } from './PDFExporter';

/** 导出文件名时间戳段：YYMMDDHHmmss（下载时刻） */
export function generateExportFileNameDateTimeSuffix(): string {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  const seconds = now.getSeconds().toString().padStart(2, '0');
  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}

function stripFilenameWhitespace(segment: string): string {
  return segment.replace(/\s+/g, '');
}

/** 选项段用于文件名：去掉空白与半角/全角圆括号 */
function sanitizeExportOptionFileNameSegment(segment: string): string {
  return stripFilenameWhitespace(segment).replace(/[()（）]/g, '');
}

export type BuildExportDownloadFileNameParams = {
  lng: SupportedLanguage;
  filmName: string | null | undefined;
  /**
   * 选项段：一般为当前导出类型在界面上的已翻译名称。
   * 若为空，使用 optionFallbackI18nKey 对应文案。
   */
  optionDisplayName?: string | null;
  /** i18n 键（export.*），在 optionDisplayName 为空时使用 */
  optionFallbackI18nKey: string;
  breakdownDisplayName?: string | null;
  /** 分场名为空时的 i18n 键，默认 export.defaultSceneName */
  breakdownFallbackI18nKey?: string;
  /** 是否去掉分场名段中的空白（与历史行为一致：仅主要角色场次 PDF 为 true） */
  stripBreakdownSegmentWhitespace?: boolean;
  extension: 'pdf' | 'xlsx';
};

/**
 * 生成导出下载文件名：{影片}-{选项}-{分场}-{时间戳}.{ext}
 * 影片名去除空白；选项名去除空白并去掉圆括号；分场名是否去除空白由 stripBreakdownSegmentWhitespace 控制。
 */
export function buildExportDownloadFileName(p: BuildExportDownloadFileNameParams): string {
  const t = (key: string) => i18n.t(key, { lng: p.lng });
  const filmPart = stripFilenameWhitespace(p.filmName ?? '');
  const optionRaw = (p.optionDisplayName?.trim()
    ? p.optionDisplayName
    : t(p.optionFallbackI18nKey)) as string;
  const optionPart = sanitizeExportOptionFileNameSegment(optionRaw);
  const breakdownKey = p.breakdownFallbackI18nKey ?? 'export.defaultSceneName';
  let breakdownPart = (p.breakdownDisplayName?.trim() ? p.breakdownDisplayName : t(breakdownKey)) as string;
  if (p.stripBreakdownSegmentWhitespace) {
    breakdownPart = stripFilenameWhitespace(breakdownPart);
  }
  const dt = generateExportFileNameDateTimeSuffix();
  return `${filmPart}-${optionPart}-${breakdownPart}-${dt}.${p.extension}`;
}
